import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { NoteStateModel } from './note.model';
import { inject, Injectable } from '@angular/core';
import { NotesService } from '../notes.service';
import { catchError, firstValueFrom, tap } from 'rxjs';
import { Note } from './note.model';
import { User } from 'app/core/user/user.types';
import { GetNotes, CreateNote, UpdateNote, DeleteNote, GetFilteredNotes, GetAllUsers, MarkNoteAsRead, OnAddNote, OnEditNote, SetNoteToEdit, OnMarkAsRead, GetFormDropdownOptions } from './note.actions';
import { OnRemoveSharedGridRowData, OnSetSharedGridRowData } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions';
import { OnCanCloseDialog, SetShowLoader, ShowToastrAlert } from 'app/modules/shared-state/shared.actions';
import { SharedGridState } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.state';
import { NoteInterface } from '../models/notes-interfaces';
import { MatDialog } from '@angular/material/dialog';
import { NoteFormComponent } from '../note-form/note-form.component';
import { ResetForm, UpdateFormValue } from '@ngxs/form-plugin';
import { DropdownOptionsService } from 'app/modules/system-setup/dropdown-options/dropdown-options.service';
import { produce } from 'immer';
import { AuthState } from 'app/modules/auth/state/auth.state';

@State<NoteStateModel>({
    name: 'notes',
    defaults: {
        notes: [],
        users: [],
        tagsDropdownForm: {},
        noteForm: {
            model: {
                documentId: '',
                comment: '',
                documentType: '',
                documentReference: '',
                tags: [],
                mentions: [],
                hasRead: false,
                status: '',
            },
            dirty: false,
            status: '',
            errors: {}
        },
        noteToEdit: null
    }
})

@Injectable()
export class NoteState {

    private _store = inject(Store);
    private _dialog = inject(MatDialog);
    private _noteService = inject(NotesService);
    private _dropdownService = inject(DropdownOptionsService);
    @Selector()
    static getAllUsers(state: NoteStateModel) {
        return state.users;
    }

    @Action(GetNotes)
    getNotes(ctx: StateContext<NoteStateModel>, { payload }: GetNotes) {
        ctx.dispatch(new SetShowLoader(true));
        return this._noteService.getNotes().pipe(
            catchError((error) => {
                console.error('Error getting notes:', error);
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((notes: Note[]) => {
                ctx.dispatch(new OnSetSharedGridRowData({ reload: true, payload: notes, gridUUID: payload.gridUUID }));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(CreateNote)
    submitNote(ctx: StateContext<NoteStateModel>) {
        ctx.dispatch(new SetShowLoader(true));
        const notesFormData = ctx.getState().noteForm.model;
        ctx.dispatch(new UpdateFormValue({
            value: {
                tags: [],
                mentions: [],
            },
            path: 'notes.noteForm'
        }));
        return this._noteService.createNote(notesFormData).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error creating note: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((notes: Note) => {
                ctx.dispatch(new OnCanCloseDialog(true));
                const gridData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID].rowData;
                const gridDataIndex = gridData.findIndex((row: any) => row._id === notesFormData.documentId);

                const updatedGridState = produce(gridData[gridDataIndex], (draft) => {
                    draft.hasNotes = true;
                });

                ctx.dispatch(new OnSetSharedGridRowData({
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
                    payload: [updatedGridState],
                    update: true
                }));
                ctx.dispatch(new OnSetSharedGridRowData({ payload: [notes], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Note created for ${notes.documentReference}`,
                    position: 'toast-top-center'
                }));
            })
        );
    }

    @Action(MarkNoteAsRead)
    markNoteAsRead(ctx: StateContext<NoteStateModel>, { payload }: MarkNoteAsRead) {
        ctx.dispatch(new SetShowLoader(true));
        return this._noteService.markNoteAsRead(payload.noteId).pipe(
            catchError((error) => {
                console.error('Error marking note as read:', error.error.message.message);
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error marking note as read: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: { status: string; }) => {
                const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);

                // Clone the gridConfig to ensure immutability
                const updatedGridConfig = {
                    ...gridConfig,
                    [payload.gridUUID]: {
                        ...gridConfig[payload.gridUUID],
                        rowData: gridConfig[payload.gridUUID].rowData.map(row =>
                            row._id === payload.noteId
                                ? { ...row, status: result.status }
                                : row
                        )
                    }
                };

                // Dispatch the updated row data to OnSetSharedGridRowData
                ctx.dispatch(
                    new OnSetSharedGridRowData({
                        reload: true,
                        payload: updatedGridConfig[payload.gridUUID].rowData,
                        gridUUID: payload.gridUUID
                    })
                );
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Note marked as read',
                    position: 'toast-top-center'
                }));
            })
        );
    }


    @Action(UpdateNote)
    updateNote(ctx: StateContext<NoteStateModel>, { payload }: UpdateNote) {
        ctx.dispatch(new SetShowLoader(true));
        const noteToEdit = ctx.getState().noteToEdit;
        const noteFormData = ctx.getState().noteForm.model;
        ctx.dispatch(new ResetForm({ path: 'notes.noteForm' }));
        return this._noteService.updateNote({
            ...noteFormData,
            _id: noteToEdit._id,
            md5Checksum: noteToEdit.md5Checksum,
        }).pipe(
            catchError((error) => {
                console.error('Error updating note:', error);
                delete noteFormData.mentions;
                delete noteFormData.tags;
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: 'Error updating note, form reset',
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new UpdateFormValue({
                    value: noteFormData,
                    path: 'notes.noteForm'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((updateNote: NoteInterface) => {
                ctx.dispatch(new OnSetSharedGridRowData(
                    {
                        payload: [updateNote],
                        gridUUID: payload ? payload : this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID,
                        update: true
                    }
                ));
                ctx.dispatch(new OnCanCloseDialog(true));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Note updated',
                    position: 'toast-top-center'
                }));
            })
        );

    }

    @Action(DeleteNote)
    deleteNote(ctx: StateContext<NoteStateModel>, { payload }: DeleteNote) {
        const createdById = payload.removeRow.createdBy.id;
        const authenticatedUser = this._store.selectSnapshot(AuthState.getAuthData) as any;
        const authenticatedUserId = authenticatedUser.sub;
        if (createdById !== authenticatedUserId) {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You are not authorized to delete this note',
                position: 'toast-top-center'
            }));
            return;
        }
        ctx.dispatch(new SetShowLoader(true));
        return this._noteService.deleteNote(payload.removeRow._id).pipe(
            catchError((error) => {
                console.error('Error deleting note:', error);
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                //** uses a shared grid state to remove the row, handle the update of the grid state to update the grid */
                ctx.dispatch(new OnRemoveSharedGridRowData({ removeRow: payload.removeRow, gridUUID: payload.gridUUID }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Note deleted',
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new OnRemoveSharedGridRowData(
                    {
                        removeRow: payload.removeRow,
                        gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID,
                    }
                ));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(GetFilteredNotes)
    getFilteredNotes(ctx: StateContext<NoteStateModel>, { payload }: GetFilteredNotes) {
        return this._noteService.getFilteredNotes(payload).pipe(
            tap((filteredNotes: Note[]) => {
                ctx.dispatch(new GetFilteredNotes(filteredNotes));
            })
        );
    }

    @Action(GetAllUsers)
    getAllUsers(ctx: StateContext<NoteStateModel>) {
        return this._noteService.getAllUsers().pipe(
            catchError((error) => {
                console.error('Error getting all users:', error);
                throw error;
            }),
            tap((users: User[]) => {
                const transformedUsers = users.map((user: any) => {
                    return {
                        id: user._id,
                        firstName: user.firstName,
                        surname: user.surname,
                        email: user.email,
                        role: user.role,
                        hasRead: false,
                        status: 'unread'
                    };
                });
                ctx.patchState({ users: transformedUsers });
            })
        );
    }

    @Action(OnAddNote)
    async onAddNote(ctx: StateContext<NoteStateModel>, { payload }: OnAddNote) {
        ctx.dispatch(new SetShowLoader(true));
        await firstValueFrom(this._store.dispatch(new GetFormDropdownOptions(['tags'])));
        this._store.dispatch(new GetAllUsers()).subscribe(() => {
            this._store.dispatch(new UpdateFormValue({
                value: {
                    documentId: payload.documentId,
                    documentReference: payload.documentReference,
                    documentType: payload.documentType,
                },
                path: 'notes.noteForm'
            }));
            this._dialog.open(NoteFormComponent, {
                width: '500px',
                data: {
                    documentId: payload.documentId,
                    documentReference: payload.documentReference,
                    documentType: payload.documentType,
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID
                },
                disableClose: true
            });
            ctx.dispatch(new SetShowLoader(false));
        });
    }

    @Action(SetNoteToEdit)
    setNoteToEdit(ctx: StateContext<NoteStateModel>, { payload }: SetNoteToEdit) {
        ctx.patchState({ noteToEdit: payload });
    }

    @Selector()
    static getNoteToEdit(state: NoteStateModel) {
        return state.noteToEdit;
    }

    @Action(OnEditNote)

    async onEditNote(ctx: StateContext<NoteStateModel>, { payload }: OnEditNote) {
        const authenticatedUser = this._store.selectSnapshot(AuthState.getAuthData) as any;
        const authenticatedUserId = authenticatedUser.sub;
        const noteToEdit = ctx.getState().noteToEdit;
        const createdById = noteToEdit.createdBy.id;
        if (createdById !== authenticatedUserId) {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You are not authorized to edit this note',
                position: 'toast-top-center'
            }));
            return;
        }

        ctx.dispatch(new SetShowLoader(true));
        await firstValueFrom(this._store.dispatch(new GetFormDropdownOptions(['tags'])));
        this._store.dispatch(new GetAllUsers()).subscribe(() => {

            this._store.dispatch(new UpdateFormValue({
                value: {
                    comment: noteToEdit.comment,
                    documentId: noteToEdit.documentId,
                    documentReference: noteToEdit.documentReference,
                    documentType: noteToEdit.documentType,
                },
                path: 'notes.noteForm'
            }));
            this._dialog.open(NoteFormComponent, {
                width: '500px',
                data: {
                    editNote: true,
                    gridUUID: payload ? payload : this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID
                },
                disableClose: true
            });
            ctx.dispatch(new SetShowLoader(false));
        });
    }


    @Action(OnMarkAsRead)
    onMarkAsRead(ctx: StateContext<NoteStateModel>, { payload }: OnMarkAsRead) {
        switch (payload.status) {
            case 'All Read':
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'warning',
                    messageTitle: 'Invalid',
                    messageBody: 'Note fully read',
                    position: 'toast-top-center'
                }));
                break;
            case 'General Note':
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'warning',
                    messageTitle: 'Invalid',
                    messageBody: 'Note cannot be marked as read',
                    position: 'toast-top-center'
                }));
                break;
            default:
                ctx.dispatch(new MarkNoteAsRead({
                    noteId: payload.noteId,
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID
                }));
        }
    }

    @Action(GetFormDropdownOptions)
    getFormDropdownOptions(ctx: StateContext<NoteStateModel>, { payload }: GetFormDropdownOptions) {
        return this._dropdownService.getDropdownsFilter(payload).pipe(
            tap((result: any) => {
                ctx.patchState({ tagsDropdownForm: result });
            })
        );
    }

    @Selector()
    static getTagsDropdownForm(state: NoteStateModel) {
        return state.tagsDropdownForm;
    }

}